import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import Button from '@mui/material/Button'
import { Divider, useTheme } from '@mui/material'
import React from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { WHOLESALE_CHECKOUT_URL } from '../../../urls'
import FormattedPrice from '@components/FormattedPrice'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import CartSummary from '@components/CartSummary'

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

const Buttons: React.FC<{
  orderId: number | null
  onSaveAsDraft(): Promise<void>
  onAddNote(): void
  onDeleteOrder(): void
  onClearShoppingBag(): Promise<void>
}> = ({ orderId, onSaveAsDraft, onAddNote, onDeleteOrder, onClearShoppingBag }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const mdUp = useMediaQuery(theme.breakpoints.up('md'))
  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Button
            component={Link}
            to={WHOLESALE_CHECKOUT_URL}
            color='secondary'
            variant='contained'
            size='large'
            disableElevation
            fullWidth
          >
            <span className='font-bold'>
              {orderId
                ? t('button.continueToCheckout', 'Continue to Checkout')
                : t('button.proceedToCheckout', 'Proceed to Checkout')}
            </span>
          </Button>
        </Grid>
        {!orderId && mdUp && (
          <Grid item xs={12}>
            <Button
              color='warning'
              variant='outlined'
              size='large'
              onClick={onClearShoppingBag}
              disableElevation
              fullWidth
            >
              <span className='font-bold'>{t('button.clearShoppingBag', 'Clear shopping bag')}</span>
            </Button>
          </Grid>
        )}
        {orderId && (
          <>
            <Grid item xs={6} md={12}>
              <Button
                color='primary'
                variant='outlined'
                size='medium'
                onClick={onSaveAsDraft}
                disableElevation
                fullWidth
              >
                <span className='font-bold'>{t('button.saveAsDraft', 'Save as Draft')}</span>
              </Button>
            </Grid>
            <Grid item xs={6} md={12}>
              <Button color='primary' variant='outlined' size='medium' onClick={onAddNote} disableElevation fullWidth>
                <span className='text-base tracking-1 font-bold'>{t('button.addNote', 'Add a note')}</span>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                color='warning'
                variant='outlined'
                size='medium'
                onClick={onDeleteOrder}
                disableElevation
                fullWidth
              >
                <span className='font-bold'>{t('button.deleteOrder', 'Delete order')}</span>
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

interface CartLinesTotalProps {
  loading: boolean
  cartQueryResult: CartQueryResult

  onSaveAsDraft(): Promise<void>

  onAddNote(): void

  onDeleteOrder(): void

  cartClear(categoryId?: string): Promise<void>
}

const CartLinesTotal: React.FC<CartLinesTotalProps> = ({
  cartQueryResult,
  loading,
  onSaveAsDraft,
  onAddNote,
  onDeleteOrder,
  cartClear,
}) => {
  const { t } = useTranslation()
  const itemSubtotal = cartQueryResult.data?.cart?.totalPrice.discount.gross
  const currency = cartQueryResult.data?.cart?.totalPrice.discount.currency || 'USD'
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  if (!cartQueryResult.data?.cart?.count) return null

  return (
    <>
      <div className='bg-white shadow-card md:sticky z-5 rounded-lg overflow-hidden mb-20' style={{ top: '120px' }}>
        {!!Object.keys(categoriesWithLines).length && (
          <>
            <p className='text-bold text-lg p-20 mb-0 mt-0 leading-tight capitalize'>
              {t('orderSummary', 'Order Summary')}
            </p>
            <Divider />
            <div className='p-20 text-13'>
              <CartSummary categoriesWithLines={categoriesWithLines} />
            </div>
            <Divider />
          </>
        )}
        <div className='p-20 flex items-center justify-between text-2xl font-bold'>
          <span>{t('estimatedTotal', 'Estimated Total')}</span>
          <span className={loading ? 'opacity-0' : ''}>
            <FormattedPrice currency={currency} amount={itemSubtotal} />
          </span>
        </div>
        <Divider />
        <div className='p-20 pb-10 text-13'>
          {t('shippingAndTaxCalculatedInCheckout', 'Shipping cost and Tax calculated in Checkout')}
        </div>
        <div className='px-20 pb-20'>
          <Buttons
            orderId={cartQueryResult.data.cart.orderId}
            onSaveAsDraft={onSaveAsDraft}
            onAddNote={onAddNote}
            onDeleteOrder={onDeleteOrder}
            onClearShoppingBag={() => cartClear()}
          />
        </div>
      </div>
      <div
        className='fixed block md:hidden bottom-0 bg-white z-20 w-full p-10 left-0 right-0'
        style={{
          boxShadow: '0 -2px 4px 0 rgb(0 0 0 / 10%), 0 0 20px 0 rgb(0 0 0 / 10%)',
        }}
      >
        <div className='font-bold text-center text-lg mb-10'>
          <span>{t('estimatedTotal', 'Estimated Total')}:&nbsp;</span>
          <span className='relative'>
            <Loader
              className='spinner spinner--xs'
              type='ThreeDots'
              color='#333'
              visible={loading}
              height={100}
              width={100}
            />
            <span className={loading ? 'opacity-0' : ''}>
              <FormattedPrice currency={currency} amount={itemSubtotal} />
            </span>
          </span>
        </div>
        <div className='pt-10'>
          <Buttons
            orderId={cartQueryResult.data.cart.orderId}
            onSaveAsDraft={onSaveAsDraft}
            onAddNote={onAddNote}
            onDeleteOrder={onDeleteOrder}
            onClearShoppingBag={() => cartClear()}
          />
        </div>
      </div>
    </>
  )
}

export default CartLinesTotal
